// Authentication Service for SwipeSage Admin Panel
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User,
  AuthError
} from 'firebase/auth';
import { getFirebaseAuth } from './firebase';
import { firestoreService } from './firestore';

// DPDP RBAC. 'super_admin' sees raw PII (income, credit score, address);
// 'support' sees those fields masked in the UI. Set via the `role` custom
// claim (scripts/setAdminClaim.js). RBAC fails CLOSED: an admin with no
// explicit role — or any token-read error — resolves to 'support' (least
// privilege). Super-admins are provisioned with an explicit
// role:'super_admin' claim by setAdminClaim.js, so only that claim unlocks
// raw PII; a transient refresh failure can never silently elevate access.
export type AdminRole = 'super_admin' | 'support'

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  isAdmin: boolean;
  role: AdminRole;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

class AuthService {
  private auth = getFirebaseAuth();

  // Sign in with email and password
  async signIn(credentials: LoginCredentials): Promise<AuthUser> {
    try {
      const userCredential = await signInWithEmailAndPassword(
        this.auth,
        credentials.email,
        credentials.password
      );
      
      const user = userCredential.user;
      
      // Check if user is admin (you can implement your own admin check logic)
      const isAdmin = await this.checkAdminStatus(user.uid);
      
      if (!isAdmin) {
        await signOut(this.auth);
        throw new Error('Access denied. Admin privileges required.');
      }

      const role = await this.getAdminRole();
      return this.transformUser(user, isAdmin, role);
    } catch (error) {
      console.error('Sign in error:', error);
      throw this.handleAuthError(error as AuthError);
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      await signOut(this.auth);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.auth.currentUser;
  }

  // Listen to auth state changes
  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        const isAdmin = await this.checkAdminStatus(user.uid);
        if (isAdmin) {
          const role = await this.getAdminRole();
          callback(this.transformUser(user, isAdmin, role));
        } else {
          callback(null);
        }
      } else {
        callback(null);
      }
    });
  }

  // Admin status is determined SOLELY by the {admin: true} custom claim on
  // the Firebase Auth ID token, set via scripts/setAdminClaim.js. The legacy
  // email-allowlist + Firestore `isAdmin` fallbacks were removed once the
  // prod claim migration was confirmed — the sole live admin holds the claim
  // and the leftover isAdmin docs were orphans. forceRefresh picks up a
  // just-granted claim without requiring a sign-out/in.
  private async checkAdminStatus(uid: string): Promise<boolean> {
    const user = this.auth.currentUser;
    if (!user) return false;

    // forceRefresh: true ensures we see claims set after the user last signed in.
    try {
      const tokenResult = await user.getIdTokenResult(true);
      if (tokenResult.claims.admin === true) {
        // Best-effort: ensure a user doc exists for first-time admins.
        this.createAdminUserInBackground(uid, user).catch(() => { /* non-blocking */ });
        return true;
      }
    } catch {
      // Token read failed (likely offline) — fail closed, treat as not-admin.
    }

    return false;
  }

  // Best-effort creation of an admin user document on first login.
  // Non-blocking — silently swallows errors.
  private async createAdminUserInBackground(uid: string, user: any): Promise<void> {
    try {
      const userData: any = {
        uid,
        email: user.email!,
        displayName: user.displayName || 'Admin User',
        createdAt: new Date(),
        updatedAt: new Date(),
        isAdmin: true,
        isActive: true,
        profile: {
          firstName: (user.displayName || 'Admin').split(' ')[0],
          lastName:  (user.displayName || 'User').split(' ')[1] || 'User',
        },
        preferences: {
          notifications: { email: true, push: true, sms: false },
          theme: 'dark',
          language: 'en',
        },
        referral: {
          referralCode:    `ADMIN_${uid.slice(0, 8).toUpperCase()}`,
          referralCount:   0,
          totalEarnings:   0,
          referralHistory: [],
        },
      };
      if (user.phoneNumber) userData.phoneNumber = user.phoneNumber;
      await firestoreService.createUser(uid, userData);
    } catch {
      // Non-blocking; swallow.
    }
  }

  // Read the RBAC role from the ID token's `role` custom claim. Fails
  // CLOSED to 'support' (least privilege) when the claim is absent, holds
  // any non-'super_admin' value, or the token read throws. This prevents a
  // transient getIdTokenResult() failure — or a mis-provisioned account
  // lacking the role claim — from silently granting raw-PII access. Only an
  // explicit role:'super_admin' claim (set by scripts/setAdminClaim.js)
  // unlocks super_admin.
  private async getAdminRole(): Promise<AdminRole> {
    const user = this.auth.currentUser;
    if (!user) return 'support';
    try {
      const tokenResult = await user.getIdTokenResult();
      return tokenResult.claims.role === 'super_admin' ? 'super_admin' : 'support';
    } catch {
      return 'support';
    }
  }

  // Transform Firebase User to AuthUser
  private transformUser(
    user: User, isAdmin: boolean, role: AdminRole,
  ): AuthUser {
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      isAdmin,
      role
    };
  }

  // Handle Firebase Auth errors
  private handleAuthError(error: AuthError): Error {
    switch (error.code) {
      case 'auth/user-not-found':
        return new Error('No account found with this email address.');
      case 'auth/wrong-password':
        return new Error('Incorrect password.');
      case 'auth/invalid-email':
        return new Error('Invalid email address.');
      case 'auth/user-disabled':
        return new Error('This account has been disabled.');
      case 'auth/too-many-requests':
        return new Error('Too many failed attempts. Please try again later.');
      case 'auth/network-request-failed':
        return new Error('Network error. Please check your connection.');
      default:
        return new Error('Authentication failed. Please try again.');
    }
  }
}

export const authService = new AuthService();
export default authService; 